import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export function ForgotPasswordForm({
  onCodeSent,
  onSwitch,
}: {
  onCodeSent: (email: string) => void;
  onSwitch: () => void;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      // Through the Worker (enumeration-safe 200 on its route). The browser
      // SDK's account.createRecovery is NOT used: it posts browser-direct to
      // Appwrite, where a stale a_session_* cookie shadows the request, and
      // Appwrite rejects reset URLs from origins not registered as web
      // platforms (workers.dev was not registered — measured 2026-09-21).
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setMessage("If the email exists, a reset link has been sent.");
      onCodeSent(email);
      setCooldown(60);
      // Countdown timer
      const interval = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            clearInterval(interval);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } catch (e: any) {
      // The route is enumeration-safe and returns 200 even for unknown
      // emails; 429 is the only user-visible outcome besides genuine 5xx
      // server failures (e.g. 500 reset_url_unconfigured). Masking those
      // as success would tell the user a link was sent when it wasn't —
      // surface them as a real error instead.
      const status = (e as Error & { status?: number })?.status;
      if (status === 429) {
        setMessage("Too many requests. Please wait before resending.");
      } else if (status !== undefined && status >= 500) {
        setMessage("Unable to send the reset link right now. Please try again later or contact your administrator.");
      } else {
        setMessage("If the email exists, a reset link has been sent.");
        onCodeSent(email);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4" aria-live="polite">
      <div>
        <h2 className="font-serif text-xl text-maroon-700">Forgot password</h2>
        <p className="text-sm text-ink-500">Enter your email to receive a reset link.</p>
      </div>
      <div>
        <Label htmlFor="fp-email">Email</Label>
        <Input
          id="fp-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          autoComplete="email"
        />
      </div>
      {message && <p className="text-sm text-ink-600">{message}</p>}
      {cooldown > 0 && (
        <p className="text-xs text-ink-400">Resend available in {cooldown}s</p>
      )}
      <Button onClick={submit} disabled={busy || !email || cooldown > 0} className="w-full">
        {busy ? "Sending…" : "Send reset link"}
      </Button>
      <button type="button" className="text-sm text-ink-500 underline" onClick={onSwitch}>
        Back to sign in
      </button>
    </div>
  );
}
